/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { ElectricalSettingsInfo } from '../../../models/electrical-settings-model';
import { Observable, Subject, Subscription, filter, take, takeUntil } from 'rxjs';
import { ElectricalSettingsShortcutInformation } from '../../../models/electrical-settings-menu-info';
import {
  ElectricalSettingsInformation,
  ElectricalSettingsObjectInformation,
} from '../../../models/electrical-settings-information';
import { ElectricalSettingsStatusChanges } from '../../../models/electrical-settings-return';
import { ElectricalSettingsShortcuts } from '../../../models/electrical-settings-shortcuts';
import {
  ELECTRICAL_SETTINGS_FACADE_TOKEN,
  IElectricalSettingsFacade,
} from '../../../../../../data-access/src/lib/domain/facades-models/electrical-settings-facade-interface';
import { ElectricalSettingsReturnCodes } from '../../../models/electrical-settings-return-codes';
import { SysqueElectricalState } from '../../../models/sysque-electrical-state';
import { GlobalConstants } from '../../../constants/global-constants';
import { AcrylicSurfaceService } from '../../../acrylic-surface/acrylic-surface.service';
import { AlertStatus, IAlert } from '../../../../../../../shared/ui/src/lib/components/alert/alert-types';

@Component({
  selector: 'sysque-electrical-settings',
  templateUrl: './electrical-settings.component.html',
  styleUrls: ['./electrical-settings.component.scss'],
  standalone: false,
})
export class ElectricalSettingsComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter();

  //#region Public properties

  public data: ElectricalSettingsInfo = {
    firstSlideName: '',
    secondSlideName: '',
    applyName: '',
    restoreName: '',
  };
  public componentTitle = '';
  public isEnableTooltips: boolean;
  public isNominalSize: boolean;
  public isWorksharingAlerts: boolean;
  public isConduitRunSelection: boolean;
  public isEditDeleteAddFitting: boolean;
  public imageDescriptionChanged: string;
  public alternateDescriptionChanged: string;
  public fittingRotation: string;
  public connectSize: string;
  public errorMessage: string | null = null;
  public activeTab: string = GlobalConstants.KeyboardShortcuts;
  public items: ElectricalSettingsShortcutInformation[];
  public optionsKeyOne: string[];
  public optionsKeyTwo: string[];
  public optionsKeyThree: string[];
  public selectedValueMap: ElectricalSettingsShortcutInformation[] = [];
  public invalidElements: { element: string; message: string }[] = [];
  public isApplyButtonClicked: boolean = false;
  public isRevertButtonClicked: boolean = false;
  public dockedState = new SysqueElectricalState();
  public isAlertVisible = false;
  public isConduitRunSelectionVisible = false;
  public releaseVersion: string;

  public electricalShortcutsOptions$: Observable<ElectricalSettingsShortcuts>;
  public electricalSettingInformation$: Observable<ElectricalSettingsInformation>;
  public electricalSettingInformationSub: Subscription;
  public electricalSettingChangesSub: Subscription;

  //#endregion

  //#region Private properties

  private state: SysqueElectricalState;
  private readonly closed$ = new Subject<void>();
  private alertHeight = 740;
  private nonAlertHeight = 650;
  private isSuccess = true;
  
  //#endregion

  //#region HTML properties

  public placeholders = [
    '',
    GlobalConstants.ModifierPlaceholder,
    GlobalConstants.KeyOnePlaceholder,
    GlobalConstants.KeyTwoPlaceholder,
  ];
  public imageDescription = [
    GlobalConstants.None,
    GlobalConstants.ImageFamilyName,
    GlobalConstants.ImageInstanceDescription,
    GlobalConstants.ImageTypeDescription,
  ];
  public alternateDescription = [
    GlobalConstants.None,
    GlobalConstants.Both,
    GlobalConstants.DescriptionInstance,
    GlobalConstants.DescriptionType,
  ];
  public tabs = [
    {
      id: GlobalConstants.KeyboardShortcuts,
      active: true,
      label: GlobalConstants.KeyboardShortcuts,
    },
    {
      id: GlobalConstants.Preferences,
      label: GlobalConstants.Preferences,
      active: false,
    },
  ];

  //#endregion

  /**
   * Get alert information
   */
  public get alert(): IAlert {
    return {
      message: GlobalConstants.ChangesAppliedSuccessfully,
      type: AlertStatus.Success,
      isDismissable: true,
    };
  }

  constructor(
    @Inject(ELECTRICAL_SETTINGS_FACADE_TOKEN) private electricalSettingsFacade: IElectricalSettingsFacade,
    private cdr: ChangeDetectorRef,
    private acrylicSurfaceService: AcrylicSurfaceService
  ) {}

  /**
   * This method is used to subscribe to the electrical settings changes
   */
  public ngOnInit(): void {
    // Set the data
    this.data.firstSlideName = GlobalConstants.KeyboardShortcuts;
    this.data.secondSlideName = GlobalConstants.Preferences;
    this.data.applyName = GlobalConstants.ApplyButton;
    this.data.restoreName = GlobalConstants.RestoreDefaultsButton;
    this.componentTitle = GlobalConstants.SysQueSettings;

    // Get electrical settings information
    this.electricalSettingsFacade
      .getElectricalSystemsInformation()
      .pipe(
        filter((result) => Object.keys(result).length > 0),
        takeUntil(this.closed$)
      )
      .subscribe((response) => {
        this.initializeElectricalSettings(response);
      });

    // Get electrical shortcuts information
    this.electricalSettingsFacade
      .getElectricalShortcutsModifiersInformation()
      .pipe(
        filter((result) => Object.keys(result).length > 0),
        takeUntil(this.closed$)
      )
      .subscribe((response) => {
        this.initializeShortcutsOptions(response.result);
      });

    this.state = this.electricalSettingsFacade.getState();
    this.releaseVersion = this.state.releaseVersion;
    if(this.isVersionGreaterThan1201(this.releaseVersion)){
      this.isConduitRunSelectionVisible = true;
    }
  }

  /**
   * This method is used to subscribe to the electrical settings changes
   */
  public ngOnDestroy(): void {
    this.closed$.next();
    this.closed$.complete();
    if (this.electricalSettingInformationSub) {
      this.electricalSettingInformationSub.unsubscribe();
    }
    if (this.electricalSettingChangesSub) {
      this.electricalSettingChangesSub.unsubscribe();
    }
  }

  /**
  * Check if the release version is greater than 12.0.1
  * @param version - the version string to check
  * @returns - true if the version is greater than 12.0.1, otherwise false
  */
  public isVersionGreaterThan1201(version: string, target: string = '12.0.1'): boolean {
    const [major, minor, patch] = version.split('.').map(Number);
    const [targetMajor, targetMinor, targetPatch] = target.split('.').map(Number);

    if (major > targetMajor) {
      return true;
    }

    if (major === targetMajor && minor > targetMinor) {
      return true;
    }
    if (major === targetMajor && minor === targetMinor && patch > targetPatch) {
      return true;
    }

    return false;
  }


  /**
   * Sets the modifier
   * @param item - item to set modifier
   * @returns - returns modifier
   */
  public setDefaultModifier(item: ElectricalSettingsShortcutInformation): string {
    item.defaultModifier = item.modifiers;
    return item.modifiers;
  }

  /**
   * Sets the key one
   * @param item - item to set key one
   * @returns - returns key one
   */
  public setDefaultKeyOne(item: ElectricalSettingsShortcutInformation): string {
    item.defaultKey1 = item.key1;
    return item.key1;
  }

  /**
   * Sets the key two
   * @param item - item to set key two
   * @returns - returns key two
   */
  public setDefaultKeyTwo(item: ElectricalSettingsShortcutInformation): string {
    item.defaultKey2 = item.key2;
    return item.key2;
  }

  /**
   * Check if the element is invalid
   * @param element - the element to check if it is invalid
   * @returns - returns true if the element is invalid, otherwise false
   */
  public isElementInvalid(element: ElectricalSettingsShortcutInformation): boolean {
    return this.invalidElements.some((item) => item.element.toLowerCase() === element.optionMenu.toLowerCase());
  }

  /**
   * This method is used to handle the tooltip change event
   */
  public onEnableTooltipClick(): void {
    this.isEnableTooltips = !this.isEnableTooltips;
  }

  /**
   * This method is used to handle the worksharing change event
   */
  public onWorksharingClick(): void {
    this.isWorksharingAlerts = !this.isWorksharingAlerts;
  }

  /**
   * This method is used to handle the edit delete add fitting change event
   */
  public onEditDeleteAddFittingClick(): void {
    this.isEditDeleteAddFitting = !this.isEditDeleteAddFitting;
  }

  /**
   * This method is used to handle the nominal size change event
   */
  public onNominalSizeClick(): void {
    this.isNominalSize = !this.isNominalSize;
  }

    /**
   * This method is used to handle the conduit run selection change event
   */
    public onConduitRunSelectionClick(): void {
      this.isConduitRunSelection = !this.isConduitRunSelection;
    }

  /**
   * This method is used to handle the fitting rotation change event
   * @param event - event to handle the fitting rotation change
   */
  public onFittingRotationChange(event: Event): void {
    const parsedValue = parseFloat((event.target as HTMLInputElement).value).toString();
    if (parsedValue && parsedValue.split('.')[1]?.length <= 4) {
      this.fittingRotation = parsedValue;
    } else {
      this.fittingRotation = null;
    }
  }

  /**
   * This method is used to handle the connect size change event
   * @param event - event to handle the connect size change
   */
  public onConnectSizeChange(event: Event): void {
    const parsedValue = parseFloat((event.target as HTMLInputElement).value).toString();
    if (parsedValue && parsedValue.split('.')[1]?.length <= 4) {
      this.connectSize = parsedValue;
    } else {
      this.connectSize = null;
    }
  }

  /**
   * This method is used to handle the image description change event
   * @param event - event to handle the image description change
   */
  public onImageDescritpionChanged(event: { value: string }): void {
    this.imageDescriptionChanged = event.value;
  }

  /**
   * This method is used to handle the alternate description change event
   * @param event - event to handle the alternate description change
   */
  public onAlternateDescritpionChanged(event: { value: string }): void {
    this.alternateDescriptionChanged = event.value;
  }

  /**
   * This method is used to handle the value change event
   * @param key - the key for which the value is changed
   * @param item - the item for which the key value is changed
   * @param event - the event to handle the value change
   */
  public onValueChanged(key: string, item: ElectricalSettingsShortcutInformation, event: any): void {
    // Reset the buttons values
    this.isApplyButtonClicked = false;

    // Check if the value is placeholder
    // If it is placeholder, then return
    if (this.placeholders.includes(event.value)) {
      return;
    }

    // Reset the revert button clicked value
    this.isRevertButtonClicked = false;

    // Find the index of the item
    const index = this.selectedValueMap.findIndex((element) => element.optionMenu === item.optionMenu);
    const finalIndex = index === -1 ? this.selectedValueMap.length : index;

    // Handle invalid elements
    this.handleInvalidElements(item, finalIndex);

    // Set selected values
    this.setSelectedValues(item, key, finalIndex, event.value);

    this.cdr.detectChanges();
  }

  /**
   * This method is used to handle the tab change event
   * @param event - event to handle the tab change
   */
  public onTabChange(event: { detail: string }) {
    this.activeTab = event.detail;
  }

  /**
   * This method is used to show error message for the invalid elements
   * @param item - item to show error message
   */
  public showErrorMessage(item: ElectricalSettingsShortcutInformation): void {
    this.invalidElements.forEach((element) => {
      if (element.element.toLowerCase() === item.optionMenu.toLowerCase()) {
        this.errorMessage = element.message;
      }
    });
  }

  /**
   * This method is used to handle the click event of the dismiss button from the alert message
   */
  public onDismissClick(): void {
    this.electricalSettingsFacade.onWindowResize(this.nonAlertHeight);
    this.isAlertVisible = false;
  }

  /**
   * This method is used to handle the click event of the apply button
   */
  public onApplyClick(): void {
    this.isRevertButtonClicked = false;
    this.isApplyButtonClicked = true;
    let electricalSettingsChanges: ElectricalSettingsInformation = {
      menuInfo: [],
      tooltips: false,
      worksharingNotification: false,
      conduitRunSelection: false,
      editDeleteAddFitting: false,
      isNominalSize: false,
      imageDescription: '',
      alternateDescription: '',
      rotationAngle: '',
      connectSize: '',
    };
    if (this.activeTab === GlobalConstants.KeyboardShortcuts) {
      this.saveKeyboardShortcuts(electricalSettingsChanges);
    } else {
      this.savePreferences(electricalSettingsChanges);
    }
    this.cdr.detectChanges();
  }

  /**
   * This method is used to handle the click event of the revert button
   */
  public onRevertClick(): void {
    this.invalidElements = [];
    // Reset the selected values
    this.selectedValueMap.forEach((item) => {
      item.modifiers = '';
      item.key1 = '';
      item.key2 = '';
    });

    // Reset the items
    this.items.forEach((item) => {
      item.modifiers = '';
      item.key1 = '';
      item.key2 = '';
      item.defaultModifier = '';
      item.defaultKey1 = '';
      item.defaultKey2 = '';
    });
    this.invalidElements = [];
    this.isRevertButtonClicked = true;
    this.isApplyButtonClicked = false;
    this.cdr.detectChanges();
  }

  /**
   * This method is used to handle the click event of the close button
   */
  public closeWindow(): void {
    this.electricalSettingsFacade.closeWindow();
    this.close.emit();
    this.acrylicSurfaceService.hide();
  }

  //#region Private methods

  /**
   * Save keyboard shortcuts
   * @param electricalSettingsChanges - electrical settings changes
   */
  private saveKeyboardShortcuts(electricalSettingsChanges: ElectricalSettingsInformation): void {
    // Validate selected values
    this.validateSelectedValues();

    // If there are invalid elements, then return
    if (this.invalidElements.length > 0) {
      return;
    }

    // If there are no invalid elements, then apply the changes
    this.isSuccess = true;
    this.updateElectricalSettingsChanges(electricalSettingsChanges);
    let count = 0;
    this.electricalSettingsFacade
      .getInvalidShortcuts(electricalSettingsChanges)
      .pipe(take(2))
      .subscribe((response) => {
        count++;
        // If the count is not 2, then return
        // Only the second time the response is the correct one
        if (count !== 2) {
          return;
        }

        // Verify return codes
        for (let item in response.result) {
          this.verifyReturnCode(response.result, item);
        }

        if (this.isSuccess) {
          // If there are noy invalid elements, then show the alert success message
          this.electricalSettingsFacade.onWindowResize(this.alertHeight);
          this.isAlertVisible = true;
        } else {
          // Otherwise, do not show the alert success message
          this.electricalSettingsFacade.onWindowResize(this.nonAlertHeight);
          this.isAlertVisible = false;
        }
      });
  }

  /**
   * Save preferences settings
   * @param electricalSettingsChanges - electrical settings changes
   */
  private savePreferences(electricalSettingsChanges: ElectricalSettingsInformation): void {
    electricalSettingsChanges.tooltips = this.isEnableTooltips;
    electricalSettingsChanges.alternateDescription = this.alternateDescriptionChanged;
    electricalSettingsChanges.connectSize = this.connectSize;
    electricalSettingsChanges.editDeleteAddFitting = this.isEditDeleteAddFitting;
    electricalSettingsChanges.imageDescription = this.imageDescriptionChanged;
    electricalSettingsChanges.rotationAngle = this.fittingRotation;
    electricalSettingsChanges.worksharingNotification = this.isWorksharingAlerts;
    electricalSettingsChanges.conduitRunSelection = this.isConduitRunSelection;
    electricalSettingsChanges.isNominalSize = this.isNominalSize;
    this.electricalSettingsFacade.applyPreferences(electricalSettingsChanges);

    this.updateStorage();

    //Show alert message
    this.electricalSettingsFacade.onWindowResize(this.alertHeight);
    this.isAlertVisible = true;
  }

  /**
   * Update storage
   */
  private updateStorage(): void {
    // Update local storage for tooltips
    localStorage.removeItem(GlobalConstants.IS_TOOLTIP_ON);
    this.electricalSettingsFacade.updateLocalStorage(GlobalConstants.IS_TOOLTIP_ON, this.isEnableTooltips);

    //Update local storage for electrical settings save success
    localStorage.removeItem(GlobalConstants.ELECTRICAL_SETTINGS_SAVE_SUCCESS);
    this.electricalSettingsFacade.updateLocalStorage(GlobalConstants.ELECTRICAL_SETTINGS_SAVE_SUCCESS, 'true');
  }

  /**
   * Verify return code
   * @param response - electrical settings status changes
   * @param item - item to verify
   */
  private verifyReturnCode(response: ElectricalSettingsStatusChanges[], item: string): void {
    let errorMessage: string;
    switch (response[item].returnCodes) {
      case ElectricalSettingsReturnCodes.KeyCombinationAnotherApplication: {
        errorMessage =
          GlobalConstants.KeyCombinationAlreadyAssignedTo +
          response[item].applicationName.toLocaleLowerCase() +
          GlobalConstants.PleaseTryAnotherKeyCombination;
        this.isSuccess = false;
        break;
      }
      case ElectricalSettingsReturnCodes.KeyCombinationExistInRevit: {
        errorMessage = GlobalConstants.KeyCombinationExistInRevit;
        this.isSuccess = false;
        break;
      }
    }

    if (errorMessage) {
      this.invalidElements.push({ element: response[item].settingName, message: errorMessage });
    }
  }

  /**
   * Update electrical settings changes
   * @param electricalSettingsChanges - electrical settings changes
   */
  private updateElectricalSettingsChanges(electricalSettingsChanges: ElectricalSettingsInformation): void {
    if (!this.selectedValueMap) {
      return;
    }

    for (let index in this.selectedValueMap) {
      electricalSettingsChanges.menuInfo.push(this.selectedValueMap[index]);
    }
  }

  /**
   * Set invalid elements
   * @param item - item for which the invalid elements are to be set
   * @param index - index of the item
   */
  private handleInvalidElements(item: ElectricalSettingsShortcutInformation, index: number): void {
    if (
      this.selectedValueMap[index] &&
      this.invalidElements.some((element) => element.element === this.selectedValueMap[index].optionMenu)
    ) {
      this.invalidElements = this.invalidElements.filter((element) => element.element !== item.optionMenu);
    }

    if (!this.selectedValueMap[index]) {
      this.selectedValueMap[index] = { optionMenu: item.optionMenu, modifiers: '', key1: '', key2: '' };
    }

    if (this.invalidElements.some((element) => element.element === this.selectedValueMap[index].optionMenu)) {
      this.invalidElements = this.invalidElements.filter((element) => element.element !== item.optionMenu);
    }

    this.resetMessageIfNeededForDuplicates(item);
  }

  /**
   * Reset message for duplicates if no longer duplicated
   * @param item - item to reset the message
   */
  private resetMessageIfNeededForDuplicates(item: ElectricalSettingsShortcutInformation): void {
    // Get all elements with the same key combination as the current item
    // Except the current item
    const duplicatedShortcuts = this.selectedValueMap.filter(
      (element) =>
        element.optionMenu !== item.optionMenu &&
        element.modifiers === item.modifiers &&
        element.key1 === item.key1 &&
        element.key2 === item.key2
    );

    // If there are more than one element with the same key combination, then return
    // because the key combination is still duplicated
    if (duplicatedShortcuts.length !== 1) {
      return;
    }

    // If there is only one element with the same key combination, then remove it from the invalid elements
    // because the key combination is not duplicated anymore
    this.invalidElements = this.invalidElements.filter(
      (element) => element.element !== duplicatedShortcuts[0].optionMenu
    );
  }

  /**
   * Set selected values
   * @param item - item for which the selected values are to be set
   * @param key - key to identify the selected value
   * @param index - index of the item
   * @param value - value to be set
   */
  private setSelectedValues(
    item: ElectricalSettingsShortcutInformation,
    key: string,
    index: number,
    value: string
  ): void {
    // Set selected values if key is 'Modifiers'
    if (key === GlobalConstants.Modifiers) {
      this.selectedValueMap[index].modifiers = item.modifiers = value !== GlobalConstants.NoValue ? value : '';
    } else if (item.defaultModifier && key !== GlobalConstants.Modifiers) {
      this.selectedValueMap[index].modifiers = item.defaultModifier;
    }

    // Set selected values if key is 'Key1'
    if (key === GlobalConstants.KeyOne) {
      this.selectedValueMap[index].key1 = item.key1 = value !== GlobalConstants.NoValue ? value : '';
    } else if (item.defaultKey1 && key !== GlobalConstants.KeyOne) {
      this.selectedValueMap[index].key1 = item.defaultKey1;
    }

    // Set selected values if key is 'Key2'
    if (key === GlobalConstants.KeyTwo) {
      this.selectedValueMap[index].key2 = item.key2 = value !== GlobalConstants.NoValue ? value : '';
    } else if (item.defaultKey2 && key !== GlobalConstants.KeyTwo) {
      this.selectedValueMap[index].key2 = item.defaultKey2;
    }
  }

  /**
   * Validate selected values
   */
  private validateSelectedValues(): void {
    this.selectedValueMap.forEach((item) => {
      // Remove item before checking if it is invalid
      this.invalidElements = this.invalidElements.filter((element) => element.element !== item.optionMenu);

      // Checks for duplicates
      this.validateKeys(item);

      // Check for combinations
      this.validateCombinations(item);

      // Check if combination already assign
      this.validateShortcut(item);
    });
  }

  /**
   * Validate if the shortcut is already assigned
   * @param item - item to validate
   */
  private validateShortcut(item: ElectricalSettingsShortcutInformation): void {
    let count = 0;
    // If key1 is empty, then check how many times this combination is assigned
    if (item.key1 !== '') {
      count = this.selectedValueMap.filter(
        (element) => element.modifiers === item.modifiers && element.key1 === item.key1 && element.key2 === item.key2
      ).length;
    }

    // If is assigned more than once, then add to invalid elements
    if (count > 1) {
      this.invalidElements.push({ element: item.optionMenu, message: GlobalConstants.KeyCombinationAlreadyAssigned });
    }
  }

  /**
   * Validate keys
   * @param item - item to validate
   */
  private validateKeys(item: ElectricalSettingsShortcutInformation): void {
    // Check if the modifiers and key1 are same
    if (item.modifiers !== '' && item.modifiers === item.key1) {
      this.invalidElements.push({ element: item.optionMenu, message: GlobalConstants.DuplicateModifiersKey1 });
    }

    // Check if the modifiers and key2 are same
    if (item.modifiers !== '' && item.modifiers === item.key2) {
      this.invalidElements.push({ element: item.optionMenu, message: GlobalConstants.DuplicateModifiersKey2 });
    }

    // Check if the key1 and key2 are same
    if (item.key1 !== '' && item.key1 === item.key2) {
      this.invalidElements.push({ element: item.optionMenu, message: GlobalConstants.DuplicateKey1Key2 });
    }
  }

  /**
   * Validate combinations
   * @param item - item to validate
   */
  private validateCombinations(item: ElectricalSettingsShortcutInformation): void {
    // Check if modifiers are empty and key1 or key2 are not empty
    if (item.modifiers === '' && (item.key1 !== '' || item.key2 !== '')) {
      this.invalidElements.push({
        element: item.optionMenu,
        message: GlobalConstants.ModifierNotFound + item.optionMenu.toLowerCase(),
      });
    }

    // Check if key1 is empty and modifiers are not empty
    if (item.modifiers !== '' && item.key1 === '') {
      this.invalidElements.push({
        element: item.optionMenu,
        message: GlobalConstants.Key1NotFound + item.optionMenu.toLowerCase(),
      });
    }
  }

  /**
   * Initialize electrical settings
   * @param response - electrical settings informations
   */
  private initializeElectricalSettings(response: ElectricalSettingsObjectInformation): void {
    let result = response.result;
    this.isEnableTooltips = result.tooltips;
    this.isWorksharingAlerts = result.worksharingNotification;
    this.isConduitRunSelection = result.conduitRunSelection;
    this.isEditDeleteAddFitting = result.editDeleteAddFitting;
    this.fittingRotation = result.rotationAngle;
    this.connectSize = result.connectSize;
    this.imageDescriptionChanged = result.imageDescription;
    this.alternateDescriptionChanged = result.alternateDescription;
    this.isNominalSize = result.isNominalSize;
    this.items = result.menuInfo;
    this.initializeSelectedValues();
  }

  /**
   * Initialize shortcuts options
   * @param response - electrical settings shortcuts
   */
  private initializeShortcutsOptions(response: ElectricalSettingsShortcuts): void {
    this.optionsKeyOne = [GlobalConstants.NoValue, ...response.shortcutsKeyOne];
    this.optionsKeyTwo = [GlobalConstants.NoValue, ...response.shortcutsKeyTwo];
    this.optionsKeyThree = [GlobalConstants.NoValue, ...response.shortcutsKeyThree];
  }

  /**
   * Initialize selected values
   */
  private initializeSelectedValues(): void {
    this.items.forEach((item) => {
      if (item.modifiers !== '') {
        this.selectedValueMap.push({
          optionMenu: item.optionMenu,
          modifiers: item.modifiers,
          key1: item.key1,
          key2: item.key2,
        });
      }
    });
  }

  //#endregion
}
